/**
 * Per-venue one-time record for the "add to home screen" coaching popup.
 * 「保存到桌面」引导弹窗的每店一次性记录。
 *
 * WHY THIS LIVES OUTSIDE VUEX (it looks like an odd choice otherwise):
 * this project persists the whole store through `vuex-persist`, and that has
 * already produced a per-venue leak in production (`forceTableInput` carrying
 * over from one venue to the next). This record is inherently per-venue, so a
 * global slice would repeat that bug. The usual escape hatch — resetting state
 * when the venue changes — is forbidden here (the project rule against calling
 * `resetToDefaults` in `_postVenue()`, which writes the reset state straight
 * back into localStorage). Hence a dedicated key indexed by venueId, read once
 * on mount and written once after the popup shows. No reactivity needed.
 * 本模块刻意不进 vuex：项目用 vuex-persist 全量持久化，已有 `forceTableInput`
 * 跨店泄漏的前科；而本状态天然按店区分，放进全局 state 必然重蹈覆辙。又因项目
 * 硬规则禁止在 `_postVenue()` 里调 `resetToDefaults`（会把 reset 后的状态写回
 * localStorage），也不能靠切店重置来隔离。所以用独立 key + venueId 索引。
 *
 * FAIL-SAFE, NEVER FAIL-OPEN: every storage access is wrapped, because Safari
 * private mode throws on write and storage can be disabled outright. On any
 * error or malformed data `hasPromptedForVenue` answers TRUE — treat it as
 * already prompted and stay silent. The popup is a nice-to-have; the ordering
 * flow is not, and a storage failure must never reach the customer.
 * 失败一律「当作已弹过」而不是「当作没弹过」：Safari 隐私模式写入会抛异常，存储
 * 也可能被完全禁用。任何异常/脏数据都返回 true 保持沉默——弹窗是锦上添花，点餐
 * 流程不是，存储故障绝不能冒到顾客面前。
 */

// The dedicated localStorage key. Holds JSON: { "<venueId>": <epochMs>, ... }.
// 独立 localStorage key，存 JSON：{ "<venueId>": <毫秒时间戳>, ... }。
export const A2HS_PROMPT_STORAGE_KEY = 'mm_a2hs_prompted'

// A customer can visit many venues over the years; keep only the newest N.
// 顾客可能陆续逛很多家店，只保留最近 N 家，防止无限增长。
export const A2HS_PROMPT_MAX_ENTRIES = 50

/**
 * Normalize a venueId into a storage key. Falsy / blank → '' (invalid), so we
 * never write an "undefined" key into the map.
 * 把 venueId 规范成 key；假值/空白 → ''（无效），避免写出 "undefined" 这种 key。
 *
 * @param {string|number} venueId
 * @returns {string} the key, or '' when the venueId is unusable
 */
function venueKey(venueId) {
  if (!venueId) return ''
  return String(venueId).trim()
}

/**
 * Read the whole record map. `{}` when nothing has been stored yet, `null`
 * when storage is unavailable or the stored blob is not a plain object.
 * 读整张记录表：从未写过 → `{}`；存储不可用或数据损坏 → `null`。
 *
 * @returns {Object|null}
 */
function readRecords() {
  let raw = null
  try {
    raw = localStorage.getItem(A2HS_PROMPT_STORAGE_KEY)
  } catch (e) {
    return null // 存储不可用（隐私模式/被禁用），保持沉默
  }
  if (raw === null || raw === undefined) return {}
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('bad shape')
    return parsed
  } catch (e) {
    // 脏数据自愈：不删的话这个坏 blob 会让本设备所有店永远「已弹过」，
    // 且唯一的写入方永远不会执行、坏数据永不翻篇。删掉后下次访问恢复正常。
    try {
      localStorage.removeItem(A2HS_PROMPT_STORAGE_KEY)
    } catch (e2) { /* 删不掉也只能沉默 */ }
    return null
  }
}

/** Coerce a stored value to a sortable timestamp; dirty entries sort oldest. */
function timestampOf(value) {
  const n = Number(value)
  return isFinite(n) ? n : 0
}

/**
 * Drop the oldest entries once the map exceeds the cap, keeping the newest
 * A2HS_PROMPT_MAX_ENTRIES by timestamp.
 * 超过上限时按时间戳裁掉最旧的，只留最近 A2HS_PROMPT_MAX_ENTRIES 家。
 *
 * @param {Object} records
 * @returns {Object} the same map, or a pruned copy
 */
function pruneRecords(records) {
  const keys = Object.keys(records)
  if (keys.length <= A2HS_PROMPT_MAX_ENTRIES) return records
  const kept = {}
  keys
    .sort((a, b) => timestampOf(records[b]) - timestampOf(records[a]))
    .slice(0, A2HS_PROMPT_MAX_ENTRIES)
    .forEach(key => { kept[key] = records[key] })
  return kept
}

/**
 * Has this venue already shown the popup? Errors and dirty data answer `true`
 * so we stay silent (see the fail-safe note at the top of this file).
 * 该店是否已弹过？异常与脏数据一律返回 true，保持沉默（见文件头的失败策略）。
 *
 * @param {string|number} venueId
 * @returns {boolean}
 */
export function hasPromptedForVenue(venueId) {
  const key = venueKey(venueId)
  if (!key) return true
  const records = readRecords()
  if (!records) return true
  return Object.prototype.hasOwnProperty.call(records, key)
}

/**
 * Record that this venue has shown the popup. A no-op for an unusable venueId,
 * and silent on write failure — a blocked write only costs us one extra popup
 * on the next visit, which is far cheaper than breaking the page.
 * 记下该店已弹过。venueId 无效则什么都不做；写入失败静默吞掉——最多下次多弹一次，
 * 远好过让页面出错。
 *
 * @param {string|number} venueId
 * @param {number} [now] - injectable clock, for deterministic tests
 */
export function markPromptedForVenue(venueId, now = Date.now()) {
  const key = venueKey(venueId)
  if (!key) return
  const records = readRecords() || {}
  records[key] = now
  try {
    localStorage.setItem(A2HS_PROMPT_STORAGE_KEY, JSON.stringify(pruneRecords(records)))
  } catch (e) {
    // Safari private mode / storage disabled / quota exceeded. Nothing to do.
    // Safari 隐私模式、存储被禁用、配额满——无事可做，静默返回。
  }
}

/** Wipe every record. Debug / test helper, not used by the ordering flow. */
export function clearPromptRecords() {
  try {
    localStorage.removeItem(A2HS_PROMPT_STORAGE_KEY)
  } catch (e) {
    // Storage unavailable; there is nothing left to clear anyway.
  }
}
